import React, { useState, useEffect } from 'react';
import { ChevronUp } from 'lucide-react';

const ScrollToTopButton: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      // Show button when page is scrolled down 300px
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    // Add scroll event listener
    window.addEventListener('scroll', toggleVisibility, { passive: true });

    // Check initial scroll position
    toggleVisibility();

    return () => {
      window.removeEventListener('scroll', toggleVisibility);
    };
  }, []);

  const scrollToTop = () => {
    setIsScrolling(true);
    
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });

    // Reset scrolling state after animation
    setTimeout(() => {
      setIsScrolling(false);
    }, 600);
  };

  return (
    <>
      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-24 right-6 z-40 bg-gray-700 hover:bg-gray-800 text-white rounded-full p-3 shadow-lg transition-all duration-300 transform ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0 pointer-events-none'
        } ${isScrolling ? 'scale-90' : 'hover:scale-110'}`}
        aria-label="Scroll to top"
        title="Scroll to top"
      >
        <ChevronUp 
          className={`w-5 h-5 transition-transform duration-300 ${
            isScrolling ? 'translate-y-[-2px]' : ''
          }`} 
        />
        
        {/* Pulse effect on hover */}
        <div className="absolute inset-0 rounded-full bg-gray-600 animate-ping opacity-0 group-hover:opacity-20"></div>
      </button>

      <style>{`
        /* Smooth appearance animation */
        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        /* Mobile adjustments */
        @media (max-width: 640px) {
          .fixed.bottom-24.right-6 {
            bottom: 90px;
            right: 20px;
          }
          
          /* Smaller button on mobile */
          button[aria-label="Scroll to top"] {
            padding: 10px;
          }
          
          button[aria-label="Scroll to top"] svg {
            width: 18px;
            height: 18px;
          }
        }

        /* Ensure button doesn't interfere with WhatsApp button */
        @media (min-width: 641px) {
          button[aria-label="Scroll to top"] {
            right: 24px;
            bottom: 100px;
          }
        }

        /* Add subtle shadow for better visibility */
        button[aria-label="Scroll to top"] {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }

        button[aria-label="Scroll to top"]:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        /* Smooth transition for all states */
        button[aria-label="Scroll to top"] {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
    </>
  );
};

export default ScrollToTopButton;